import {
  Component,
  inject,
  signal,
  HostListener,
  ElementRef,
  input,
  effect,
} from '@angular/core';
import {
  RouterLink,
  RouterOutlet,
  Router,
  NavigationEnd,
  ActivatedRoute,
} from '@angular/router';
import { CommonModule } from '@angular/common';
import { MenuItem } from './menu-item.interface';
import { MenuService } from './menu.service';
import { filter } from 'rxjs/operators';
import { AuthService } from '@auth/services/auth.service';
import { IconComponent } from '@ui/icon/icon.component';
import { PageHeaderService } from './page-header.service';
import { NotificationService } from '@ui/notification/notification.service';
import { UserMenuComponent } from './user-menu/user-menu.component';
import { MunicipalitySwitcherComponent } from './municipality-switcher/municipality-switcher.component';
import { LoadingService } from './loading-service';
import { AppService } from '../../app.service';

interface BreadcrumbItem {
  name: string;
  path: string;
}
@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    RouterOutlet,
    IconComponent,
    UserMenuComponent,
    MunicipalitySwitcherComponent,
  ],
  template: `
    @if(loadingService.isLoading()) {
    <div
      class="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center"
    >
      <div class="bg-white rounded-lg p-6 shadow-lg flex items-center gap-3">
        <div
          class="w-6 h-6 border-4 border-primary-600 border-t-transparent rounded-full animate-spin"
        ></div>
        <span class="text-slate-800">Laden...</span>
      </div>
    </div>
    }

    <div class="min-h-screen flex">
      <!-- Sidebar -->
      <aside [class.collapsed]="isMenuCollapsed()" class="sidebar-container">
        <div class="p-3 flex justify-center items-center">
          <h1
            class="text-lg font-semibold mr-auto"
            [class.hidden]="isMenuCollapsed()"
          >
            {{ appName() }}
          </h1>
          <button
            (click)="toggleMenu()"
            class="hover:text-primary-600 w-8 h-8 flex items-center justify-center"
          >
            <ui-icon
              [name]="isMenuCollapsed() ? 'menu' : 'menu-open'"
            ></ui-icon>
          </button>
        </div>

        <!-- Menu Items -->
        <nav class="flex-1 overflow-y-auto pb-4">
          @if (municipalityMenuItems().length) {
            <p class="menu-section-label menu-section-first">Gemeente</p>
            <ul class="space-y-1">
              @for (item of municipalityMenuItems(); track item.path) {
                <li>
                  @if (isMunicipalityMenuItemDisabled(item)) {
                    <span
                      class="menu-item menu-item-disabled"
                      [attr.title]="'Selecteer eerst een gemeente'"
                    >
                      <ui-icon [name]="item.icon || 'circle'" [size]="'base'"></ui-icon>
                      <span class="menu-label">{{ item.label }}</span>
                    </span>
                  } @else {
                    <a
                      [routerLink]="item.path"
                      class="menu-item"
                      [class.active]="router.isActive(item.path, false)"
                    >
                      <ui-icon [name]="item.icon || 'circle'" [size]="'base'"></ui-icon>
                      <span class="menu-label">{{ item.label }}</span>
                    </a>
                  }
                </li>
              }
            </ul>
          }

          @if (generalMenuItems().length) {
            <p
              class="menu-section-label"
              [class.menu-section-first]="municipalityMenuItems().length === 0"
              [class.mt-6]="municipalityMenuItems().length > 0"
            >
              Algemeen
            </p>
            <ul class="space-y-1">
              @for (item of generalMenuItems(); track item.path) {
                <li>
                  <a
                    [routerLink]="item.path"
                    class="menu-item"
                    [class.active]="router.isActive(item.path, false)"
                  >
                    <ui-icon [name]="item.icon || 'circle'" [size]="'base'"></ui-icon>
                    <span class="menu-label">{{ item.label }}</span>
                  </a>
                </li>
              }
            </ul>
          }
        </nav>
      </aside>

      <!-- Main Content -->
      <main class="flex-1 bg-slate-50 flex flex-col h-screen overflow-hidden">
        <div class="flex flex-col flex-1 overflow-hidden">
          <!-- Header -->
          <header
            class="bg-white shadow-sm rounded px-4 py-3 mb-4 flex-shrink-0 flex justify-between items-center h-[60px]"
          >
            <div class="flex items-center gap-3">
              <!-- <button
                *ngIf="pageHeader.showBackButton()"
                (click)="goBack()"
                class="text-slate-600 hover:text-slate-800 transition-colors flex items-center gap-1"
              >
                <ui-icon name="arrow-back" [size]="'base'"></ui-icon>
                <span class="text-sm">Terug</span>
              </button> -->
              <h2 class="text-xl font-medium text-slate-800">
                <span *ngFor="let breadcrumb of breadcrumbs(); let last = last">
                  <a
                    [routerLink]="breadcrumb.path"
                    class="hover:text-primary-600 transition-colors"
                    [class.cursor-pointer]="!last"
                    [class.cursor-default]="last"
                    [class.hover:text-slate-800]="last"
                  >
                    {{ breadcrumb.name }}
                  </a>
                  <span *ngIf="!last" class="mx-2">/</span>
                </span>
              </h2>
            </div>

            <!-- Right side with user menu only -->
            <div class="relative flex justify-end items-center gap-4">
              <app-municipality-switcher></app-municipality-switcher>
              <button
                (click)="toggleUserMenu()"
                class="flex items-center gap-2 text-slate-600 hover:text-slate-800 p-2 rounded-full hover:bg-slate-100"
              >
                <ui-icon name="person" [size]="'base'"></ui-icon>
                <ui-icon name="expand_more" [size]="'sm'"></ui-icon>
              </button>

              <!-- User Dropdown Menu -->
              <app-user-menu
                [isOpen]="isUserMenuOpen"
                (isOpenChange)="isUserMenuOpen = $event"
              ></app-user-menu>
            </div>
          </header>

          <!-- Content -->
          <div class="flex-1 overflow-auto px-4">
            <router-outlet></router-outlet>
          </div>
        </div>
      </main>
    </div>
  `,
  styles: [
    `
      .sidebar-container {
        @apply w-56 bg-primary-25 border-r border-slate-200 flex flex-col transition-all duration-300;
      }

      .sidebar-container.collapsed {
        @apply w-16;
      }

      .menu-item {
        @apply text-slate-600 flex gap-3 px-4 py-3 text-sm hover:bg-primary-500/50 hover:text-primary-600 transition-colors rounded-lg mx-2;
      }

      .collapsed .menu-item {
        @apply px-2 mx-1 justify-center;
      }

      .collapsed .menu-label {
        @apply hidden;
      }

      .collapsed .menu-item:hover {
        @apply relative text-primary-600;
      }

      .collapsed .menu-item:hover .menu-label {
        @apply block absolute left-full ml-2 bg-primary-400/50 text-primary-600 px-3 py-2 rounded whitespace-nowrap z-50;
      }

      .collapsed .menu-item span.material-icons {
        @apply flex items-center justify-center;
      }

      .active {
        @apply bg-primary-400/50 text-primary-600;
      }

      .menu-section-label {
        @apply px-4 pt-4 pb-1 text-xs font-semibold uppercase tracking-wide text-slate-500;
      }

      .menu-section-first {
        @apply mt-8;
      }

      .collapsed .menu-section-label {
        @apply hidden;
      }

      .menu-item-disabled {
        @apply cursor-not-allowed opacity-50 hover:bg-transparent hover:text-slate-600;
      }

      @keyframes spin {
        to {
          transform: rotate(360deg);
        }
      }
    `,
  ],
})
export class AppLayoutComponent {
  protected menuService = inject(MenuService);
  protected router = inject(Router);
  protected authService = inject(AuthService);
  protected pageHeader = inject(PageHeaderService);
  protected notificationService = inject(NotificationService);
  protected activatedRoute = inject(ActivatedRoute);
  protected loadingService = inject(LoadingService);
  protected appService = inject(AppService);

  appName = input.required<string>();

  generalMenuItems = signal<MenuItem[]>([]);
  municipalityMenuItems = signal<MenuItem[]>([]);
  isMenuCollapsed = signal<boolean>(
    JSON.parse(localStorage.getItem('menuCollapsed') || 'false')
  );

  isUserMenuOpen = false;
  breadcrumbs = signal<BreadcrumbItem[]>([]);

  municipalityId = this.appService.municipalityId;

  // Voeg click handler toe om submenu's te sluiten bij klik buiten
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isUserMenuOpen = false;
    }
  }

  constructor(private elementRef: ElementRef) {
    // Haal menu items op
    //this.menuItems.set(this.menuService.getMenuItems());

    // Luister naar route wijzigingen
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.breadcrumbs.set(
            this.createBreadcrumbs(this.activatedRoute.root)
          );
        }
      });

    effect(
      () => {
        this.authService.currentUser();
        this.appService.municipalityId();
        this.appService.isKeyUser();
        this.appService.isConsulent();
        this.updateMenuItems();
      },
      { allowSignalWrites: true }
    );
  }

  updateMenuItems(): void {
    const groups = this.menuService.getMenuGroups();
    this.generalMenuItems.set(groups.general);
    this.municipalityMenuItems.set(groups.municipality);
  }

  isMunicipalityMenuItemDisabled(item: MenuItem): boolean {
    if (this.municipalityId()) {
      return false;
    }

    if (
      item.path === '/dashboard' &&
      (this.authService.isAdmin() || this.authService.isBackoffice())
    ) {
      return false;
    }

    return true;
  }

  logout(): void {
    this.authService.logout();
    this.router.navigate(['/login']);
  }

  toggleMenu(): void {
    const newState = !this.isMenuCollapsed();
    this.isMenuCollapsed.set(newState);
    localStorage.setItem('menuCollapsed', JSON.stringify(newState));
  }

  goBack(): void {
    if (this.pageHeader.backTo()) {
      this.router.navigate([this.pageHeader.backTo()]);
    } else {
      this.router.navigate(['..']);
    }
  }

  toggleUserMenu(): void {
    this.isUserMenuOpen = !this.isUserMenuOpen;
  }

  private createBreadcrumbs(
    route: ActivatedRoute,
    url: string = '',
    breadcrumbs: BreadcrumbItem[] = []
  ): BreadcrumbItem[] {
    const children = route.children;

    if (children.length === 0) {
      return breadcrumbs;
    }

    let currentUrl = '';
    for (const child of children) {
      const routeURL = child.snapshot.url
        .map((segment) => segment.path)
        .join('/');

      // Update URL only if there's a path segment
      const nextUrl = routeURL !== currentUrl ? `${url}/${routeURL}` : url;

      // Get route data
      const label = child.snapshot.data['name'];
      if (label) {
        const cleanUrl = '/' + nextUrl.replace(/^\/+/, '').replace(/\/+/g, '/');
        // Check if this URL is different from the last breadcrumb's URL
        const lastBreadcrumb = breadcrumbs[breadcrumbs.length - 1];
        if (!lastBreadcrumb || lastBreadcrumb.path !== cleanUrl) {
          breadcrumbs.push({
            name: label,
            path: cleanUrl,
          });
        }
      }

      // Recursively process child routes
      this.createBreadcrumbs(child, nextUrl, breadcrumbs);
    }

    return breadcrumbs;
  }
}
